import { useState } from 'react';
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Pagination,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import type { SelectChangeEvent } from '@mui/material/Select';
import { useQuery } from '@tanstack/react-query';

import { adminApi } from '../../services/adminApi';

const WINDOW_OPTIONS: { value: number; label: string }[] = [
  { value: 24, label: 'Last 24 hours' },
  { value: 168, label: 'Last 7 days' },
  { value: 720, label: 'Last 30 days' },
];
const PAGE_SIZE = 25;

function formatTimestamp(value: string): string {
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? value : d.toLocaleString();
}

/**
 * Admin scrape-job / results viewing page (GitHub #12 area 2).
 *
 * READ-ONLY history of recent scrape runs reconstructed from raw listings:
 * retailer, run time, status, and per-run listing counts. There is no
 * "trigger scrape" control — this surface never starts a live scrape.
 *
 * Auth is enforced upstream by AdminLayout (redirects non-admins); the server
 * also re-checks admin role. Operational metadata only — no end-user PII.
 */
export function ScrapeJobsPage() {
  const [windowHours, setWindowHours] = useState(168);
  const [page, setPage] = useState(1);

  const query = useQuery({
    queryKey: ['admin', 'scrape-jobs', { windowHours, page }],
    queryFn: () => adminApi.listScrapeJobs({ windowHours, page, limit: PAGE_SIZE }),
  });

  function onWindowChange(event: SelectChangeEvent<string>) {
    setWindowHours(Number(event.target.value));
    setPage(1);
  }

  return (
    <Box>
      <Typography variant="h1" sx={{ fontSize: '1.6rem', mb: 1 }}>
        Scrape jobs
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
        Read-only history of recent scrape runs. Viewing only — scrapes are run by the pipeline, not
        triggered here.
      </Typography>

      <Paper
        sx={{ p: 2, mb: 2, display: 'flex', gap: 1, alignItems: 'flex-end', flexWrap: 'wrap' }}
        elevation={0}
        variant="outlined"
      >
        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel id="scrape-jobs-window-label">Time window</InputLabel>
          <Select
            labelId="scrape-jobs-window-label"
            id="scrape-jobs-window"
            value={String(windowHours)}
            label="Time window"
            onChange={onWindowChange}
          >
            {WINDOW_OPTIONS.map((w) => (
              <MenuItem key={w.value} value={String(w.value)}>
                {w.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Paper>

      {query.isLoading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress aria-label="Loading scrape jobs" />
        </Box>
      )}

      {query.isError && (
        <Alert severity="error" role="alert">
          Failed to load scrape jobs: {(query.error as Error).message}
        </Alert>
      )}

      {query.isSuccess && (
        <>
          <TableContainer component={Paper} variant="outlined">
            <Table aria-label="Scrape jobs">
              <TableHead>
                <TableRow>
                  <TableCell>Retailer</TableCell>
                  <TableCell>Run time</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Scraped</TableCell>
                  <TableCell>Processed</TableCell>
                  <TableCell>Unprocessed</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {query.data.jobs.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6}>
                      <Typography variant="body2" color="text.secondary">
                        No scrape runs in the selected window.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  query.data.jobs.map((job) => (
                    <TableRow key={`${job.retailerId}-${job.runAt}`} hover>
                      <TableCell>{job.retailerName ?? job.retailerId}</TableCell>
                      <TableCell>{formatTimestamp(job.runAt)}</TableCell>
                      <TableCell>
                        <Chip
                          label={job.status}
                          color={job.status === 'completed' ? 'success' : 'warning'}
                          size="small"
                          sx={{ textTransform: 'capitalize' }}
                        />
                      </TableCell>
                      <TableCell>{job.listingsScraped}</TableCell>
                      <TableCell>{job.listingsProcessed}</TableCell>
                      <TableCell>{job.listingsUnprocessed}</TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
            <Pagination
              count={Math.max(1, Math.ceil(query.data.pagination.total / PAGE_SIZE))}
              page={page}
              onChange={(_e, p) => setPage(p)}
              aria-label="Scrape jobs pagination"
            />
          </Box>
        </>
      )}
    </Box>
  );
}

export default ScrapeJobsPage;
